export enum AgentMode {
    Agent = "agent",
    Spec = "spec",
    Ralph = "ralph",
    ClaudeCode = "claude-code",
    Codex = "codex",
    CodexApp = "codex-app",
}

export function cycleAgentMode(current: AgentMode, availableModes: AgentMode[]): AgentMode {
    const currentIndex = availableModes.indexOf(current);
    if (currentIndex === -1) {
        return availableModes[0];
    }
    const nextIndex = (currentIndex + 1) % availableModes.length;
    return availableModes[nextIndex];
}

const RALPH_COMMAND_PREFIX = "/ona:ralph";
const SPEC_COMMAND_PREFIX = "/ona:spec";

/**
 * Applies the agent mode prefix to a prompt based on the selected mode.
 */
export function applyAgentModePrefix(prompt: string, agentMode: AgentMode): string {
    if (agentMode === AgentMode.Ralph) {
        return `${RALPH_COMMAND_PREFIX} ${prompt}`;
    }
    if (agentMode === AgentMode.Spec) {
        return `${SPEC_COMMAND_PREFIX} ${prompt}`;
    }
    return prompt;
}

/**
 * Display information for each agent mode.
 */
export const AGENT_MODE_INFO: Record<AgentMode, { label: string; description: string }> = {
    [AgentMode.Agent]: {
        label: "Agent",
        description: "For executing multi-step work.",
    },
    [AgentMode.Spec]: {
        label: "Plan",
        description: "For defining requirements and intent.",
    },
    [AgentMode.Ralph]: {
        label: "Ralph",
        description: "For iterative work with a feedback loop.",
    },
    [AgentMode.ClaudeCode]: {
        label: "Claude Code",
        description: "Anthropic's terminal-based coding agent.",
    },
    [AgentMode.Codex]: {
        label: "Codex",
        description: "OpenAI's terminal-based coding agent.",
    },
    [AgentMode.CodexApp]: {
        label: "Codex (Native)",
        description: "OpenAI Codex via native app-server protocol.",
    },
};

/**
 * Returns the display label for an agent mode.
 */
export function getAgentModeLabel(mode: AgentMode): string {
    return AGENT_MODE_INFO[mode]?.label ?? "Agent";
}

/**
 * Returns a brief description for an agent mode.
 */
export function getAgentModeDescription(mode: AgentMode): string {
    return AGENT_MODE_INFO[mode]?.description ?? "";
}

/**
 * Returns the placeholder text for the chat input based on the agent mode.
 */
export function getAgentModePlaceholder(mode: AgentMode): string {
    switch (mode) {
        case AgentMode.Spec:
            return "Outline requirements, constraints, and success criteria";
        case AgentMode.Ralph:
            return "Describe goals, constraints, or priorities";
        case AgentMode.Agent:
        default:
            return "Describe your task or type / for commands";
    }
}

/**
 * Returns the raw CSS color value for the flood-fill wrapper pseudo-element.
 */
export function getAgentModeFloodBgVar(mode: AgentMode): string {
    switch (mode) {
        case AgentMode.Spec:
            return "rgb(var(--surface-prompt-spec) / var(--surface-prompt-spec-opacity))";
        case AgentMode.Ralph:
            return "rgb(var(--surface-prompt-ralph) / var(--surface-prompt-ralph-opacity))";
        case AgentMode.Agent:
        default:
            return "rgb(var(--surface-muted) / var(--surface-muted-opacity))";
    }
}
